import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import blogPosts from '../data/blogPosts';
import { theme, media } from '../styles/theme';
import {
  PageContainer,
  PageTitle,
  Section,
  Text,
} from '../styles/components';

const PostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${theme.spacing.md};

  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

const PostCard = styled(Link)`
  display: block;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  text-decoration: none;
  color: inherit;
  box-shadow: ${theme.shadows.card};
  transition: box-shadow ${theme.transitions.fast}, transform ${theme.transitions.fast};

  &:hover {
    box-shadow: ${theme.shadows.cardHover};
    transform: translateY(-2px);
  }
`;

const PostDate = styled.span`
  display: block;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.lightText};
  margin-bottom: ${theme.spacing.xs};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PostTitle = styled.h2`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
  line-height: 1.3;
`;

const PostExcerpt = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.lightText};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.sm};
`;

const ReadMore = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.primary};
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};
`;

const Tag = styled.span`
  font-size: ${theme.fontSizes.xs};
  background-color: ${theme.colors.background};
  color: ${theme.colors.lightText};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  padding: 0.15rem 0.6rem;
`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const BlogPage: React.FC = () => {
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Tryllepapp blogg',
    url: 'https://selg.tryllepapp.no/blogg',
    itemListElement: blogPosts.map((post, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `https://selg.tryllepapp.no/blogg/${post.slug}`,
      name: post.title,
    })),
  };

  return (
    <Layout>
      <Helmet>
        <title>Blogg om Magic: The Gathering | Tryllepapp</title>
        <meta
          name="description"
          content="Tips, nyheter og guider om Magic: The Gathering — kjøp og salg av MTG-kort i Norge."
        />
        <link rel="canonical" href="https://selg.tryllepapp.no/blogg" />
        <meta property="og:title" content="Blogg om Magic: The Gathering | Tryllepapp" />
        <meta
          property="og:description"
          content="Tips, nyheter og guider om Magic: The Gathering — kjøp og salg av MTG-kort i Norge."
        />
        <meta property="og:url" content="https://selg.tryllepapp.no/blogg" />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <PageContainer>
        <PageTitle>Blogg</PageTitle>

        <Section>
          <Text>Tips, nyheter og guider om Magic: The Gathering og korthandel i Norge.</Text>
        </Section>

        <Section>
          <PostGrid>
            {blogPosts.map((post) => (
              <PostCard key={post.slug} to={`/blogg/${post.slug}`}>
                <PostDate>{formatDate(post.date)}</PostDate>
                <PostTitle>{post.title}</PostTitle>
                <PostExcerpt>{post.excerpt}</PostExcerpt>
                {post.tags && post.tags.length > 0 && (
                  <TagList>
                    {post.tags.map((tag) => (
                      <Tag key={tag}>{tag}</Tag>
                    ))}
                  </TagList>
                )}
                <ReadMore>Les mer →</ReadMore>
              </PostCard>
            ))}
          </PostGrid>
        </Section>
      </PageContainer>
    </Layout>
  );
};

export default BlogPage;
